import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Building, MessageSquare, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from "@/components/ui/use-toast";
import { SchemaOrg } from '@/components/utils/SchemaOrg';

const ContactPage = () => {
  const location = useLocation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pageTitle = "Contact WebFiny: Get in Touch with Our Team";
  const pageDescription = "Reach out to WebFiny for support, sales inquiries, partnership opportunities, or any questions about our PDF APIs and document automation solutions.";
  const canonicalUrl = `${window.location.origin}${location.pathname}`;
  const wyomingAddress = "4509 Driftwood Dr, Cheyenne, WY 82009, United States";
  const wyomingMapLink = "https://www.openstreetmap.org/?mlat=41.1346&mlon=-104.7830#map=17/41.1346/-104.7830";


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const mailtoLink = `mailto:info@webfiny.com?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company || 'N/A'}\n\nMessage:\n${formData.message}`
    )}`;

    try {
      window.location.href = mailtoLink;
      await new Promise(resolve => setTimeout(resolve, 500)); 

      toast({
        title: "📬 Email Client Opened",
        description: "Your email client should have opened. Please review and send your message.",
        className: "bg-blue-600 text-white border-blue-700"
      });
    } catch (error) {
      console.error("Failed to open mailto link:", error);
      toast({
        title: "⚠️ Error",
        description: "Could not open your email client. Please copy the details manually.",
        variant: "destructive"
      });
    } finally {
      setFormData({ name: '', email: '', company: '', subject: '', message: '' });
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: <Mail className="w-8 h-8 text-primary" />,
      title: "Email Us",
      details: [
        { label: "General Inquiries", value: "info@webfiny.com", href: "mailto:info@webfiny.com" },
        { label: "Sales Department", value: "sales@webfiny.com", href: "mailto:sales@webfiny.com" },
        { label: "Technical Support", value: "support@webfiny.com", href: "mailto:support@webfiny.com" },
      ],
      ariaLabel: "Email WebFiny for inquiries"
    },
    {
      icon: <Phone className="w-8 h-8 text-primary" />,
      title: "Call Us",
      details: [
        { label: "Main Office Line", value: "+1 (307) 555-0123" }, 
        { label: "Support Hotline (24/7 for Enterprise)", value: "+1 (307) 555-0199" }, 
      ],
      note: "Mon-Fri, 9 AM - 5 PM (MST)",
      ariaLabel: "Call WebFiny for support or sales"
    },
    {
      icon: <Building className="w-8 h-8 text-primary" />,
      title: "Our Office",
      details: [
        { label: "WebFiny Operations", value: wyomingAddress },
      ],
      mapLink: wyomingMapLink,
      ariaLabel: "Find WebFiny's office location"
    },
  ];

  return (
    <>
      <SchemaOrg
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": pageTitle,
            "description": pageDescription,
            "url": canonicalUrl,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonicalUrl
            },
            "publisher": {
              "@type": "Organization",
              "name": "WebFiny",
               "logo": {
                "@type": "ImageObject",
                "url": "https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png"
              },
              "contactPoint": [
                {
                  "@type": "ContactPoint",
                  "telephone": "+1-307-555-0123",
                  "contactType": "customer support",
                  "areaServed": "Worldwide",
                  "availableLanguage": "English",
                  "email": "info@webfiny.com"
                },
                 {
                  "@type": "ContactPoint",
                  "telephone": "+1-307-555-0199",
                  "contactType": "technical support",
                  "contactOption": "TollFree",
                  "areaServed": "Worldwide",
                  "availableLanguage": "English",
                  "email": "support@webfiny.com"
                }
              ],
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "4509 Driftwood Dr",
                "addressLocality": "Cheyenne",
                "addressRegion": "WY",
                "postalCode": "82009",
                "addressCountry": "US"
              }
            }
          }
        ]}
      />
      <div className="bg-gradient-to-b from-background to-muted/30 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-secondary mb-4">
              Get in Touch
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
              We're here to help! Whether you have a question about our PDF APIs, need support, or want to discuss a partnership, please don't hesitate to reach out.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="bg-card p-8 rounded-xl shadow-2xl space-y-8"
            >
              <h2 className="text-3xl font-semibold text-foreground mb-6 flex items-center">
                <MessageSquare className="w-8 h-8 mr-3 text-primary" /> Send Us a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-muted-foreground">Full Name</Label>
                    <Input type="text" name="name" id="name" placeholder="Your Name" value={formData.name} onChange={handleChange} required className="mt-1 bg-background/70 border-border focus:border-primary" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-muted-foreground">Email Address</Label>
                    <Input type="email" name="email" id="email" placeholder="you@example.com" value={formData.email} onChange={handleChange} required className="mt-1 bg-background/70 border-border focus:border-primary" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="company" className="text-sm font-medium text-muted-foreground">Company (Optional)</Label>
                  <Input type="text" name="company" id="company" placeholder="Your Company Name" value={formData.company} onChange={handleChange} className="mt-1 bg-background/70 border-border focus:border-primary" />
                </div>
                <div>
                  <Label htmlFor="subject" className="text-sm font-medium text-muted-foreground">Subject</Label>
                  <Input type="text" name="subject" id="subject" placeholder="How can we help?" value={formData.subject} onChange={handleChange} required className="mt-1 bg-background/70 border-border focus:border-primary" />
                </div>
                <div>
                  <Label htmlFor="message" className="text-sm font-medium text-muted-foreground">Message</Label>
                  <Textarea name="message" id="message" rows="5" placeholder="Your detailed message..." value={formData.message} onChange={handleChange} required className="mt-1 bg-background/70 border-border focus:border-primary" />
                </div>
                <Button type="submit" size="lg" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold text-lg shadow-lg hover:shadow-primary/40 transition-all duration-300 transform hover:scale-105" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </div>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" /> Send Message
                    </>
                  )}
                </Button>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="space-y-8"
            >
              <h2 className="text-3xl font-semibold text-foreground mb-6 flex items-center">
                <HelpCircle className="w-8 h-8 mr-3 text-primary" /> Other Ways to Connect
              </h2>
              {contactMethods.map((method, index) => (
                <div key={index} className="bg-card p-6 rounded-xl shadow-lg hover:shadow-accent/20 transition-shadow duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 mt-1">{method.icon}</div>
                    <div>
                      <h3 className="text-xl font-semibold text-foreground mb-2">{method.title}</h3>
                      {method.details.map((detail, i) => (
                        <p key={i} className="text-muted-foreground mb-1">
                          <span className="font-medium text-foreground/90">{detail.label}:</span>{' '}
                          {detail.href ? (
                            <a href={detail.href} className="hover:text-primary hover:underline transition-colors" aria-label={`${method.ariaLabel} - ${detail.label}`}>
                              {detail.value}
                            </a>
                          ) : (
                            detail.value
                          )}
                        </p>
                      ))}
                      {method.note && <p className="text-xs text-muted-foreground/80 mt-1">{method.note}</p>}
                      {method.mapLink && (
                        <a 
                          href={method.mapLink} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="inline-flex items-center text-sm text-primary hover:underline mt-2"
                          aria-label={`View ${method.details[0].value} on map`}
                        >
                          <MapPin className="w-4 h-4 mr-1" /> View on Map
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="bg-card p-6 rounded-xl shadow-lg hover:shadow-accent/20 transition-shadow duration-300">
                <h3 className="text-xl font-semibold text-foreground mb-3">Frequently Asked Questions</h3>
                <p className="text-muted-foreground mb-3">
                  Many common questions are answered in our FAQ section. Check it out before reaching out!
                </p>
                <Button variant="outline" asChild>
                  <a href="/#faq" aria-label="Visit WebFiny's FAQ section">
                    Visit FAQ Section
                  </a>
                </Button>
              </div>

            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactPage;